<script setup lang="ts">
import NavbarLanguage from '@/layouts/components/NavbarLanguage.vue'
import NavbarNotification from '@/layouts/components/NavbarNotification.vue'
import NavbarUser from '@/layouts/components/NavbarUser.vue'

import { useDisplay } from 'vuetify'
const config = useRuntimeConfig()
const { user, resetUser, isAuth } = useAuthStore()
const { t } = useI18n()

const props = defineProps({
    activeId: String
})

const display = useDisplay()

const route = useRoute()
const router = useRouter()

const sidebar = ref(false)

watch(
    () => display,
    () => {
        return display.mdAndUp ? (sidebar.value = false) : sidebar.value
    },
    { deep: true }
)
</script>

<template>
    <!-- 👉 Navigation drawer for mobile devices  -->
    <VNavigationDrawer      
        class="custom-drawer"
        :scrim="false"
        :width="$vuetify.display.mobile ? 500 : 400"
        location="right" 
        v-model="sidebar" disable-resize-watcher
    >
        <!-- Nav items -->
        <div>
            <div class="d-flex flex-column gap-y-4 pa-4">
                <!-- <VAppBarTitle>
                    <NuxtLink to="/" class="d-flex gap-x-4">
                        <div class="d-flex gap-x-3 align-center">
                            <VNodeRenderer :nodes="themeConfig.app.logo" />
                        </div>
                    </NuxtLink>
                </VAppBarTitle> -->
                <template v-if="!isAuth">
                    <NuxtLink to="/login"  class="nav-link font-weight-medium">
                        {{ t('login') }}
                    </NuxtLink>
                    <NuxtLink to="/register"  class="nav-link font-weight-medium">
                        {{ t('register') }}
                    </NuxtLink>
                </template>
                    
                <template v-if="isAuth">
                    <NuxtLink
                        to="/dashboard"
                        class="nav-link font-weight-medium"
                    >
                        {{ t('dashboard') }}
                    </NuxtLink>
                    <NuxtLink to="/job" class="nav-link font-weight-medium">
                        {{ t('all_jobs') }}
                    </NuxtLink>
                    <NuxtLink
                        to="/my-job/applied"
                        class="nav-link font-weight-medium"
                        :class="
                            route.name === 'my-job-tab'
                                ? 'router-link-active'
                                : ''
                        "
                    >
                        {{ t('my_job_list') }}
                    </NuxtLink>
                    <NuxtLink to="/profile" class="nav-link font-weight-medium">
                        {{ t('profile') }}
                    </NuxtLink>
                </template>
                <template v-else>
                    <NuxtLink to="/job" class="nav-link font-weight-medium">
                        {{ t('all_jobs') }}
                    </NuxtLink>
                    <!-- <NuxtLink
                        :href="config.public.companyUrl"
                        target="_blank"
                        class="nav-link font-weight-medium"
                    >
                        {{ t('for_company') }}
                    </NuxtLink> -->
                    <div class="d-none d-md-flex flex-column gap-y-2">
                        <NuxtLink to="/login">
                            <VBtn variant="elevated" color="primary" block>{{
                                t('login')
                            }}</VBtn>
                        </NuxtLink>
                        <NuxtLink to="/register">
                            <VBtn variant="outlined" color="primary" block>{{
                                t('register')
                            }}</VBtn>
                        </NuxtLink>
                    </div>
                </template>
            </div>
        </div>

        <!-- Navigation drawer close icon -->
        <!-- <VIcon
            id="navigation-drawer-close-btn"
            icon="ri-close-line"
            size="20"
            @click="sidebar = !sidebar"
        /> -->
        <template #append>
            <div class="pa-4">
                <LanguageSwitcherMobile />
            </div>
        </template>
    </VNavigationDrawer>

    <!-- 👉 Navbar for desktop devices  -->

    <div class="front-page-navbar">
        <VAppBar class="border-b-sm" elevation="0">
            <!-- Title and Landing page sections -->
            <div class="d-flex align-center">
                <VAppBarTitle>
                    <NuxtLink
                        to="/"
                        aria-label="Samurai Rekrutmen homepage"
                        class="d-flex gap-x-4"
                    >
                        <div class="d-flex gap-x-3 align-center">
                            <img src="/images/logo.png" alt="Samurai Logo" class="app-logo" />
                        </div>
                    </NuxtLink>
                </VAppBarTitle>
            </div>

            <VSpacer />
            <div v-if="isAuth">
                <div class="text-base align-center gap-x-2 d-flex">
                    <NuxtLink
                        v-if="$vuetify.display.mdAndUp"
                        to="/dashboard"
                        class="nav-link font-weight-medium mx-3"
                    >
                        {{ t('dashboard') }}
                    </NuxtLink>
                    <NuxtLink
                        v-if="$vuetify.display.mdAndUp"
                        to="/job"
                        class="nav-link font-weight-medium mx-3"
                    >
                        {{ t('all_jobs') }}
                    </NuxtLink>
                    <NuxtLink
                        v-if="$vuetify.display.mdAndUp"
                        to="/my-job/applied"
                        class="nav-link font-weight-medium mx-3"
                        :class="
                            route.name === 'my-job-tab'
                                ? 'router-link-active'
                                : ''
                        "
                    >
                        {{ t('my_job_list') }}
                    </NuxtLink>
                    <NuxtLink
                        v-if="$vuetify.display.mdAndUp"
                        to="/profile"
                        class="nav-link font-weight-medium mx-2"
                        :class="
                            route.name.substr(0, 16) === 'complete-profile'
                                ? 'router-link-active'
                                : ''
                        "
                    >
                        {{ t('profile') }}
                    </NuxtLink>
                    <div v-if="$vuetify.display.mdAndUp" class="divider"></div>
                    <NavbarLanguage class="d-none d-md-block" />
                    <NavbarNotification class="mx-2" />
                    <NavbarUser />
                    
                    <VBtn
                        variant="outlined"
                        :class="[
                            $vuetify.display.mdAndUp ? 'd-none' : 'd-inline-block',
                            'hamburger-button',
                            'ms-2'
                        ]"
                        icon
                        :aria-label="sidebar ? t('close_menu') : t('open_menu')"
                        @click="sidebar = !sidebar"
                    >
                        <VIcon :icon="sidebar ? 'ri-close-line' : 'ri-menu-line'"/>
                    </VBtn>
                </div>
            </div>
            <div v-else>
                <div
                    :class="$vuetify.display.mdAndUp ? 'd-flex' : 'd-none'"
                    class="text-base align-center gap-x-2"
                >
                    <NuxtLink
                        to="/job"
                        class="nav-link font-weight-medium mx-3 w-full"
                    >
                        {{ t('all_jobs') }}
                    </NuxtLink>
                    <NuxtLink
                        :href="config.public.companyUrl"
                        target="_blank"
                        class="nav-link font-weight-medium"
                    >
                        {{ t('for_company') }}
                    </NuxtLink>
                    <div class="divider"></div>
                    <NavbarLanguage class="me-2" />
                    <NuxtLink to="/login">
                        <VBtn variant="outlined">{{ t('login') }}</VBtn>
                    </NuxtLink>
                    <NuxtLink to="/register">
                        <VBtn color="primary" variant="elevated">{{
                            t('register')
                        }}</VBtn>
                    </NuxtLink>
                </div>
                
                <div :class="$vuetify.display.mdAndUp ? 'd-none' : 'd-flex gap-2 align-center'">
                   

                    <VBtn
                        variant="outlined"
                        :class="[
                            $vuetify.display.mdAndUp ? 'd-none' : 'd-inline-block',
                            'hamburger-button'
                        ]"
                        icon
                        :aria-label="sidebar ? t('close_menu') : t('open_menu')"
                        @click="sidebar = !sidebar"
                    >
                        <VIcon :icon="sidebar ? 'ri-close-line' : 'ri-menu-line'"/>
                    </VBtn>
                </div>
            </div>
        </VAppBar>
    </div>
</template>

<style lang="scss" scoped>
    .samurai-logo{
        max-width: 100px;
    }
   .custom-drawer {
        top: 81px !important;
        height: calc(100vh - 81px) !important;
        border-top: none !important;
        box-shadow: none;
    }
.front-page-navbar-box-shadow {
    /* stylelint-disable-next-line max-line-length */
    // box-shadow: 0 4px 8px -4px rgba(var(--v-shadow-key-umbra-color), 42%) !important;
}
.nav-menu {
    display: flex;
    gap: 3rem;
}

.nav-title {
    color: rgba(
        var(--v-theme-on-surface),
        var(--v-high-emphasis-opacity)
    ) !important;
    font-size: 1.25rem;
    font-weight: 600;
    letter-spacing: 0.15px;
    line-height: 1.5rem;
}

.nav-link {
    border-block-end: 1px solid rgb(var(--v-theme-pure-white));
    inline-size: fit-content;

    // padding-inline: 0.625rem;
    // margin-block: 0;
    // margin-inline: 10px;
    padding-block: 5px;
    padding-inline: 0;
    transition: border-color 0.3s ease;

    &:not(:hover) {
        color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    }

    &:hover {
        border-block-end: 1px solid rgba(var(--v-theme-secondary));
    }

    &.router-link-active {
        border-block-end: 1px solid rgba(var(--v-theme-secondary));
    }
}

// :deep(.v-btn--active > .v-btn__overlay) {
//     opacity: unset !imp;
// }

// .v-btn--active > .v-btn__overlay {
// }

:deep(.v-btn--active::before) {
    opacity: 0 !important;
}

.divider {
    block-size: 40px;
    border-inline-start: 1px solid
        rgba(var(--v-theme-grey-300), var(--v-high-emphasis-opacity));
    inline-size: 1px;
    margin-block: 0;
    margin-inline: 10px;
}

@media (min-width: 1920px) {
    .front-page-navbar {
        // border-bottom: 1px solid
        //     rgba(var(--v-theme-grey-300), var(--v-high-emphasis-opacity));
        // .v-toolbar {
        //     max-inline-size: calc(1440px - 32px);
        // }
    }
}

@media (min-width: 1280px) and (max-width: 1919px) {
    .front-page-navbar {
        // .v-toolbar {
        //     max-inline-size: calc(1200px - 32px);
        // }
    }
}

@media (min-width: 960px) and (max-width: 1279px) {
    .front-page-navbar {
        // .v-toolbar {
        //     max-inline-size: calc(900px - 32px);
        // }
    }

    .nav-menu {
        gap: 2rem;
    }
}

@media (min-width: 600px) and (max-width: 959px) {
    .front-page-navbar {
        .v-toolbar {
            // max-inline-size: calc(100% - 64px);
        }
    }
}

@media (max-width: 600px) {
    .front-page-navbar {
        .v-toolbar {
            // max-inline-size: calc(100% - 32px);
        }
    }
}

.nav-item-img {
    border: 10px solid rgb(var(--v-theme-background));
    border-radius: 10px;
}

.active-link {
    color: rgb(var(--v-theme-primary)) !important;
}

.mega-menu-item {
    &:not(:hover) {
        color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    }
}
</style>

<style lang="scss">
@use '@layouts/styles/mixins' as layoutMixins;

.mega-menu {
    position: fixed !important;
    inset-block-start: 4.1rem;
    inset-inline-start: 50%;
    transform: translateX(-50%);
}

.front-page-navbar {
    .v-toolbar__content {
        padding-inline: 2.5rem !important;
        height: 81px !important;
    }

    .v-toolbar {
        inset-inline: 0 !important;
        margin-inline: auto !important;
    }
}

#navigation-drawer-close-btn {
    position: absolute;
    cursor: pointer;
    inset-block-start: 0.5rem;
    inset-inline-end: 1rem;
}

@media (max-width: 600px) {
    .front-page-navbar {
        .v-toolbar__content {
            padding-inline: 0.75rem !important;
        }
    }
}

@media (min-width: 600px) and (max-width: 959px) {
    .front-page-navbar {
        .v-toolbar__content {
            padding-inline: 1rem !important;
        }
    }
}

.hamburger-button {
  // Atur border-radius sesuai keinginan (misal 12px)
  border-radius: 8px;
  
  // Ketebalan border (gunakan 1px atau 2px agar terlihat tegas)
  border: 1.5px solid rgba(0, 0, 0, 0.87);
  
  // Pastikan ukuran kotak proporsional
  width: 44px;
  height: 44px;
  min-width: 44px;
  
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;

  :deep(.v-icon) {
    // Ukuran icon di dalamnya
    font-size: 24px;
    color: #000;
  }
}

@media only screen and (max-width: 767px) {
}
</style>
